/**
 * @file 働く姿 (資格の試験の先の働き方) を画面に出すための純関数を置く。
 *
 * @what: 活かし方と負荷の言い方、年収の幅の文字を決める。
 * @why: 画面 (CareersPanel) と同じファイルに置くと、部品だけを出すという約束から外れる。
 * @failure: 年収を出せない働き方があるので、幅が無いときは null を返す。呼び手が空欄を出す。
 */

import type { StudyTextbookRouteCareer } from "../api/textbookRoutes";

/** 資格の活かし方の言い方を決める。 */
export const CAREER_USE_LABELS: Record<StudyTextbookRouteCareer["use"], string> = {
  requirement: "法律上の要件",
  knowledge: "知識",
  title: "肩書",
};

/** 負荷の段の言い方を決める (1=低 / 2=中 / 3=高)。 */
const LOAD_LABELS: Record<number, string> = { 1: "低", 2: "中", 3: "高" };

/**
 * 年収の幅を1つの文字にする。
 *
 * @what: 上下がそろえば "432〜641万円"、同じ値なら "714万円"、上だけなら "641万円まで"、
 *   下だけなら "432万円から"、どちらも無ければ null を返す。
 * @why: 求人の掲載額が無い働き方があり、年収を出せないまま並べる必要がある。
 */
export function incomeRangeLabel(c: StudyTextbookRouteCareer): string | null {
  const from = typeof c.income_from === "number" && c.income_from > 0 ? c.income_from : null;
  const to = typeof c.income_to === "number" && c.income_to > 0 ? c.income_to : null;
  if (from !== null && to !== null) return from === to ? `${from}万円` : `${from}〜${to}万円`;
  if (to !== null) return `${to}万円まで`;
  if (from !== null) return `${from}万円から`;
  return null;
}

/** 負荷を言い方にする (未設定は null)。 */
export function careerLoadLabel(v: number | undefined): string | null {
  if (typeof v !== "number" || v <= 0) return null;
  return LOAD_LABELS[v] ?? null;
}

/** 資格が法律上の要件になる働き方の数を返す。 */
export function requirementCareerCount(careers: readonly StudyTextbookRouteCareer[]): number {
  return careers.filter((c) => c.use === "requirement").length;
}
